import { PrimaryButton } from "components/Button/style";
import { LoadingScreen } from "components/LoadingScreen";
import { TooltipBootstrap } from "components/TooltipBootstrap";
import { Page } from "containers/Page";
import { useState } from "react";
import {
  FaArrowLeft,
  FaExchangeAlt,
  FaFileSignature,
  FaUndo,
} from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ModalRetorno, useModalRetorno } from "../../components/ModalRetorno";
import {
  ABA_LOTES,
  ROTA_SOLICITACAO_PLACA,
} from "../../constants/navegacaoSolicitacaoPlaca";
import { IDadosAutorizacaoManual } from "../../interfaces/IAcoesLote";
import { IItemLote } from "../../interfaces/IItemLote";
import {
  IEstadoAberturaLote,
  IEstadoVoltaSolicitacaoPlaca,
} from "../../interfaces/INavegacaoSolicitacaoPlaca";
import { AlterarPrensaModal } from "../../modals/AlterarPrensaModal";
import { AutorizacaoManualModal } from "../../modals/AutorizacaoManualModal";
import { FinalizarSenatranModal } from "../../modals/FinalizarSenatranModal";
import { ReprocessarModal } from "../../modals/ReprocessarModal";
import {
  elegivelParaAlterarPrensa,
  loteContinental,
  mensagemAlteracaoPrensa,
  nadaAlterado,
} from "../../utils/alterarPrensaLote";
import {
  elegivelParaFinalizarSenatran,
  nadaFinalizado,
} from "../../utils/finalizarSenatranLote";
import {
  emFalha,
  idsParaReprocessar,
  retornoReprocessamento,
} from "../../utils/reprocessarLote";
import { Filtros } from "./containers/Filtros";
import { AcoesLote } from "./containers/Filtros/style";
import { Listar } from "./containers/Listar";
import { useItensLote } from "./hooks/useItensLote";

type ModalAberto =
  | "reprocessar"
  | "autorizacaoManual"
  | "alterarPrensa"
  | "finalizarSenatran"
  | null;

export function VisualizarLote() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    itens,
    totalRegistros,
    carregando,
    limite,
    reiniciarPaginacao,
    aplicarFiltros,
    setPagina,
    mudarLimite,
    ordenar,
    exportar,
    exportando,
    reprocessar,
    reprocessando,
    informarManual,
    informandoManual,
    alterarPrensa,
    alterandoPrensa,
    finalizarSenatran,
    finalizandoSenatran,
  } = useItensLote(Number(id));
  const [selecionados, setSelecionados] = useState<IItemLote[]>([]);
  const [limparSelecao, setLimparSelecao] = useState(0);
  const [modalAberto, setModalAberto] = useState<ModalAberto>(null);
  const fecharModal = () => setModalAberto(null);
  const { mostrarRetorno, propsModalRetorno } = useModalRetorno();
  const processando =
    reprocessando || informandoManual || alterandoPrensa || finalizandoSenatran;
  const itemManual =
    selecionados.length === 1 && emFalha(selecionados[0])
      ? selecionados[0]
      : null;
  const loteNaoContinental = itens.length > 0 && !loteContinental(itens);
  const permiteAcaoContinental =
    loteContinental(itens) && selecionados.length > 0;
  const veioDaAbaLotes = Boolean(
    (location.state as IEstadoAberturaLote | null)?.daAbaLotes
  );

  function voltar() {
    if (veioDaAbaLotes) {
      navigate(-1);
      return;
    }

    const volta: IEstadoVoltaSolicitacaoPlaca = { aba: ABA_LOTES };
    navigate(ROTA_SOLICITACAO_PLACA, { state: volta });
  }

  function concluirAcao() {
    setSelecionados([]);
    setLimparSelecao((valor) => valor + 1);
  }

  async function confirmarReprocessamento(): Promise<boolean> {
    const ids = idsParaReprocessar(selecionados, itens);

    if (!ids.length) {
      return false;
    }

    const resultado = await reprocessar(ids);

    if (!resultado) {
      return false;
    }

    concluirAcao();

    const { mensagem, tipo } = retornoReprocessamento(resultado);
    mostrarRetorno("Reprocessar", mensagem, tipo);

    return true;
  }

  async function confirmarManual(
    dados: IDadosAutorizacaoManual
  ): Promise<boolean> {
    if (!itemManual || !(await informarManual(itemManual.id, dados))) {
      return false;
    }

    concluirAcao();
    mostrarRetorno(
      "Informar autorização manual",
      "Autorização confirmada com sucesso!"
    );
    return true;
  }

  async function confirmarAlteracaoPrensa(prensa: number): Promise<boolean> {
    const ids = selecionados
      .filter(elegivelParaAlterarPrensa)
      .map((item) => item.id);

    if (!ids.length) {
      return false;
    }

    const resultado = await alterarPrensa(ids, prensa);

    if (!resultado) {
      return false;
    }

    concluirAcao();

    if (nadaAlterado(resultado)) {
      mostrarRetorno(
        "Alterar prensa",
        "Nenhuma placa elegível para alterar a prensa",
        "aviso"
      );
    } else if (!resultado.alteradas) {
      mostrarRetorno(
        "Alterar prensa",
        "Nenhuma placa teve a prensa alterada.",
        "erro"
      );
    } else if (resultado.falhas || resultado.ignoradas) {
      mostrarRetorno(
        "Alterar prensa",
        mensagemAlteracaoPrensa(resultado),
        "aviso"
      );
    } else {
      mostrarRetorno("Alterar prensa", "Prensa alterada com sucesso!");
    }

    return true;
  }

  async function confirmarFinalizacaoSenatran() {
    const ids = selecionados
      .filter(elegivelParaFinalizarSenatran)
      .map((item) => item.id);

    if (!ids.length) {
      return null;
    }

    const resultado = await finalizarSenatran(ids);

    if (!resultado) {
      return null;
    }

    concluirAcao();

    if (nadaFinalizado(resultado)) {
      mostrarRetorno(
        "Finalizar Senatran",
        "Nenhuma placa elegível para finalizar no Senatran",
        "aviso"
      );
    } else if (!resultado.finalizadas) {
      mostrarRetorno(
        "Finalizar Senatran",
        "Não foi possível realizar a finalização no senatran",
        "erro"
      );
    }

    return resultado;
  }

  const acoes = (
    <AcoesLote>
      <PrimaryButton type="button" outline variante="primary" onClick={voltar}>
        <FaArrowLeft />
        Voltar
      </PrimaryButton>
      <PrimaryButton
        type="button"
        outline
        variante="primary"
        onClick={() => setModalAberto("reprocessar")}
      >
        <FaUndo />
        Reprocessar
      </PrimaryButton>
      <TooltipBootstrap
        show={!itemManual}
        margem="0.125rem"
        childrenToolTip={
          <span>Permitido apenas para placas com status de Falha</span>
        }
      >
        <span>
          <PrimaryButton
            type="button"
            outline
            variante="primary"
            disabled={!itemManual}
            onClick={() => setModalAberto("autorizacaoManual")}
          >
            <FaFileSignature />
            Autorização manual
          </PrimaryButton>
        </span>
      </TooltipBootstrap>
      <TooltipBootstrap
        show={!permiteAcaoContinental}
        margem="0.125rem"
        childrenToolTip={
          <span>
            {loteNaoContinental
              ? "Alteração de prensa disponível só para lotes Continental"
              : "Permitido apenas para placas com status Pendente"}
          </span>
        }
      >
        <span>
          <PrimaryButton
            type="button"
            outline
            variante="primary"
            disabled={!permiteAcaoContinental}
            onClick={() => setModalAberto("alterarPrensa")}
          >
            <FaExchangeAlt />
            Alterar prensa
          </PrimaryButton>
        </span>
      </TooltipBootstrap>
      <TooltipBootstrap
        show={!permiteAcaoContinental}
        margem="0.125rem"
        childrenToolTip={
          <span>
            {loteNaoContinental
              ? "Finalização no Senatran disponível só para lotes Continental"
              : "Selecione as placas para finalizar no Senatran"}
          </span>
        }
      >
        <span>
          <PrimaryButton
            type="button"
            disabled={!permiteAcaoContinental}
            onClick={() => setModalAberto("finalizarSenatran")}
          >
            Finalizar Senatran
          </PrimaryButton>
        </span>
      </TooltipBootstrap>
    </AcoesLote>
  );

  return (
    <Page lastPage="Home / Inova" title="Solicitação de placa">
      <LoadingScreen mostrar={processando} zIndex={2000} />
      <ModalRetorno {...propsModalRetorno} />
      <ReprocessarModal
        show={modalAberto === "reprocessar"}
        handleClose={fecharModal}
        porSelecao={selecionados.length > 0}
        quantidadeElegivel={idsParaReprocessar(selecionados, itens).length}
        onConfirmar={confirmarReprocessamento}
        carregando={reprocessando}
      />
      <AutorizacaoManualModal
        show={modalAberto === "autorizacaoManual"}
        handleClose={fecharModal}
        idAutorizacaoInicial={itemManual?.idAutorizacao ?? ""}
        onConfirmar={confirmarManual}
        carregando={informandoManual}
      />
      <AlterarPrensaModal
        show={modalAberto === "alterarPrensa"}
        handleClose={fecharModal}
        selecionados={selecionados}
        onConfirmar={confirmarAlteracaoPrensa}
        carregando={alterandoPrensa}
      />
      <FinalizarSenatranModal
        show={modalAberto === "finalizarSenatran"}
        handleClose={fecharModal}
        selecionados={selecionados}
        onConfirmar={confirmarFinalizacaoSenatran}
        carregando={finalizandoSenatran}
      />
      <div className="mt-4">
        <Filtros
          titulo={`Lote ${id}`}
          acoes={acoes}
          onBuscar={aplicarFiltros}
        />
      </div>
      <Listar
        itens={itens}
        totalRegistros={totalRegistros}
        carregando={carregando}
        limite={limite}
        reiniciarPaginacao={reiniciarPaginacao}
        limparSelecao={limparSelecao}
        onMudarPagina={setPagina}
        onMudarLimite={mudarLimite}
        onOrdenar={ordenar}
        onExportar={exportar}
        exportando={exportando}
        setSelecionados={setSelecionados}
      />
    </Page>
  );
}
